import { useMemo, memo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useDateRange } from '../../contexts/DateRangeContext';
import { useEnergyHistory, useWeatherHistory } from '../../hooks/useHistoricalData';
import { usePeakLoad } from '../../hooks/usePeakLoad';
import { LoadingSkeleton } from '../../components/atoms/LoadingSkeleton/LoadingSkeleton';
import { Card } from '../../components/atoms/Card/Card';
import { FullscreenChart, useIsFullscreen } from '../../components/molecules/FullscreenChart/FullscreenChart';
import { CHART_CONFIG, shouldAnimate } from '../../constants';
import type { EnergyTimeSeries } from '../../../types/energy';
import type { WeatherAverage } from '../../../types/weather';
import './EnergyGenerationChart.css';

// Cache for formatted time strings to avoid repeated expensive toLocaleString calls
const timeFormatCache = new Map<string, string>();

// Helper to format timestamps for chart display with caching
function formatChartTime(ts: string, todayDateStr: string): string {
  const cacheKey = `${ts}-${todayDateStr}`;
  const cached = timeFormatCache.get(cacheKey);
  if (cached) return cached;

  const date = new Date(ts);
  const isToday = date.toLocaleDateString("en-US", { timeZone: "America/Halifax" }) === todayDateStr;

  let result: string;
  if (isToday) {
    result = date.toLocaleTimeString("en-US", {
      timeZone: "America/Halifax",
      hour: "numeric",
      minute: "2-digit",
    });
  } else {
    result = date.toLocaleString("en-US", {
      timeZone: "America/Halifax",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  // Limit cache size to prevent memory leaks
  if (timeFormatCache.size > 5000) {
    timeFormatCache.clear();
  }
  timeFormatCache.set(cacheKey, result);
  return result;
}

// Binary search to find nearest weather reading - O(log n) instead of O(n)
function findNearestWeather(
  sortedWeather: Array<{ timestamp: number; data: WeatherAverage }>,
  targetTime: number
): WeatherAverage | undefined {
  if (sortedWeather.length === 0) return undefined;
  if (sortedWeather.length === 1) return sortedWeather[0].data;

  let left = 0;
  let right = sortedWeather.length - 1;

  // Binary search for closest timestamp
  while (left < right - 1) {
    const mid = Math.floor((left + right) / 2);
    if (sortedWeather[mid].timestamp <= targetTime) {
      left = mid;
    } else {
      right = mid;
    }
  }

  // Compare left and right to find nearest
  const leftDiff = Math.abs(sortedWeather[left].timestamp - targetTime);
  const rightDiff = Math.abs(sortedWeather[right].timestamp - targetTime);

  return leftDiff <= rightDiff ? sortedWeather[left].data : sortedWeather[right].data;
}

// Inner component for chart content - memoized to prevent unnecessary re-renders
interface ChartContentProps {
  chartData: Array<Record<string, number | string | undefined>>;
  peakLoadData: { value: number } | undefined;
  peakValue: number | undefined;
  animationsEnabled: boolean;
}

const EnergyChartContent = memo(function EnergyChartContent({ chartData, peakLoadData, peakValue, animationsEnabled }: ChartContentProps) {
  const isFullscreen = useIsFullscreen();

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? "100%" : 400}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={CHART_CONFIG.gridStroke} />
        <XAxis
          dataKey="time"
          stroke={CHART_CONFIG.axisStroke}
          tick={{ fill: CHART_CONFIG.axisStroke }}
        />
        <YAxis
          yAxisId="left"
          stroke="#f0f6fc"
          tick={{ fill: CHART_CONFIG.axisStroke }}
          label={{
            value: "MW",
            angle: -90,
            position: "insideLeft",
            fill: CHART_CONFIG.axisStroke,
          }}
          domain={[-40, peakLoadData ? Math.ceil((peakLoadData.value + 20) / 50) * 50 : 350]}
          tickCount={15}
          tickFormatter={(value: number) => Math.round(value).toString()}
        />
        <Tooltip
          contentStyle={CHART_CONFIG.tooltipStyle}
          formatter={(value: number, name: string, props: { payload?: { avgTemp?: number; avgWind?: number } }) => {
            if (name === "Avg Temperature") {
              const realTemp = props.payload?.avgTemp;
              return realTemp != null
                ? [`${Math.round(realTemp)}°C`, "Avg Temperature"]
                : [Math.round(value), name];
            }
            if (name === "Avg Wind Speed") {
              const realWind = props.payload?.avgWind;
              return realWind != null
                ? [`${Math.round(realWind)} km/h`, "Avg Wind Speed"]
                : [Math.round(value), name];
            }
            if (name.startsWith("Peak")) {
              return [`${Math.round(value)} MW`, "Peak Load"];
            }
            return [`${Math.round(value)} MW`, name];
          }}
        />
        <Legend wrapperStyle={{ color: CHART_CONFIG.axisStroke }} />
        {peakValue && (
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="peakLoad"
            stroke="#f85149"
            strokeWidth={2}
            strokeDasharray="8 4"
            dot={false}
            name={`Peak (${Math.round(peakValue)} MW)`}
            isAnimationActive={false}
          />
        )}
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="Total On-Island Load"
          stroke="#f0f6fc"
          strokeWidth={CHART_CONFIG.strokeWidth}
          dot={false}
          name="Total Load"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="Total On-Island Wind Generation"
          stroke="#3fb950"
          strokeWidth={CHART_CONFIG.strokeWidth}
          dot={false}
          name="Wind Generation"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="Total On-Island Solar plus Battery Generation"
          stroke="#a371f7"
          strokeWidth={CHART_CONFIG.strokeWidth}
          dot={false}
          name="Solar + Battery"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="Total On-Island Fossil Fuel Generation"
          stroke="#d97706"
          strokeWidth={CHART_CONFIG.strokeWidth}
          dot={false}
          name="Fossil Fuel"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="Import from NB"
          stroke="#ff7b72"
          strokeWidth={CHART_CONFIG.strokeWidth}
          dot={false}
          name="Import from NB"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="scaledTemp"
          stroke="#ffa657"
          strokeWidth={CHART_CONFIG.strokeWidth}
          strokeDasharray="5 5"
          dot={false}
          name="Avg Temperature"
          connectNulls={true}
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="scaledWind"
          stroke="#58a6ff"
          strokeWidth={CHART_CONFIG.strokeWidth}
          strokeDasharray="5 5"
          dot={false}
          name="Avg Wind Speed"
          connectNulls={true}
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
      </LineChart>
    </ResponsiveContainer>
  );
});

export function EnergyGenerationChart() {
  const { startDate, endDate } = useDateRange();
  const { data: energyResponse, isLoading: energyLoading } = useEnergyHistory(startDate, endDate);
  const { data: weatherResponse } = useWeatherHistory(startDate, endDate);
  const { data: peakLoadData } = usePeakLoad();

  // Defensive: handle both old (raw array) and new ({ data, meta }) API formats
  const energyHistory = Array.isArray(energyResponse)
    ? energyResponse
    : (energyResponse?.data ?? []);
  const weatherHistory = Array.isArray(weatherResponse)
    ? weatherResponse
    : (weatherResponse?.data ?? []);

  const peakValue = peakLoadData?.value;

  const chartData = useMemo(() => {
    // Pre-compute today's date string once for all formatting calls
    const todayDateStr = new Date().toLocaleDateString("en-US", { timeZone: "America/Halifax" });

    // Pre-sort weather data by timestamp for binary search - O(n log n) once
    const sortedWeather = weatherHistory
      .map((w: WeatherAverage) => ({
        timestamp: new Date(w.timestamp).getTime(),
        data: w,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    // Single-pass grouping using Map - O(n) instead of O(n²) with find()
    const energyByTimestamp = new Map<string, Record<string, number | string | undefined>>();

    energyHistory.forEach((curr: EnergyTimeSeries) => {
      let dataPoint = energyByTimestamp.get(curr.timestamp);
      if (!dataPoint) {
        const energyTime = new Date(curr.timestamp).getTime();
        const nearestWeather = findNearestWeather(sortedWeather, energyTime);

        dataPoint = {
          timestamp: curr.timestamp,
          time: formatChartTime(curr.timestamp, todayDateStr),
        };

        // Add weather data if available
        if (nearestWeather) {
          dataPoint.avgTemp = nearestWeather.avgTemperature;
          dataPoint.avgWind = nearestWeather.avgWindSpeed;
          dataPoint.scaledTemp =
            nearestWeather.avgTemperature != null
              ? nearestWeather.avgTemperature * 3
              : undefined;
          dataPoint.scaledWind =
            nearestWeather.avgWindSpeed != null
              ? nearestWeather.avgWindSpeed * 2
              : undefined;
        }

        // Add peak load if available
        if (peakValue) {
          dataPoint.peakLoad = peakValue;
        }

        energyByTimestamp.set(curr.timestamp, dataPoint);
      }

      // Add gauge value
      dataPoint[curr.gaugeType] = curr.value;
    });

    // Convert Map to sorted array and calculate NB import in single pass
    const result = Array.from(energyByTimestamp.values())
      .sort((a, b) => {
        const tsA = a.timestamp as string;
        const tsB = b.timestamp as string;
        return tsA.localeCompare(tsB);
      })
      .map((d) => {
        // Calculate NB import inline
        const load = (d["Total On-Island Load"] as number) || 0;
        const wind = (d["Total On-Island Wind Generation"] as number) || 0;
        const solar = (d["Total On-Island Solar plus Battery Generation"] as number) || 0;
        const fossil = (d["Total On-Island Fossil Fuel Generation"] as number) || 0;
        const totalGeneration = wind + solar + fossil;
        d["Import from NB"] = Math.max(0, load - totalGeneration);
        return d;
      });

    return result;
  }, [energyHistory, weatherHistory, peakValue]);

  // Determine if animations should be enabled based on data size
  const animationsEnabled = shouldAnimate(chartData.length);

  return (
    <Card className="chart-container">
      <FullscreenChart title="Energy Generation Over Time (MW)">
        <h3>Energy Generation Over Time (MW)</h3>
        {energyLoading ? (
          <LoadingSkeleton variant="chart" />
        ) : chartData.length > 0 ? (
          <EnergyChartContent
            chartData={chartData}
            peakLoadData={peakLoadData}
            peakValue={peakValue}
            animationsEnabled={animationsEnabled}
          />
        ) : (
          <p className="no-data">No energy data for selected range</p>
        )}
      </FullscreenChart>
    </Card>
  );
}
